import React, { useState, useEffect } from 'react';

const FoundersNote = ({ foundersNote = {} }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const steps = foundersNote?.steps?.length ? foundersNote.steps : [
    { num: '1', title: 'Read Everything', desc: "Don't cherry-pick the good parts. The insights that sting the most are often the most valuable." },
    { num: '2', title: 'Prioritize Ruthlessly', desc: 'Focus on high-impact fixes first. Small improvements in critical areas yield massive returns.' },
    { num: '3', title: 'Execute Relentlessly', desc: 'Knowledge without action is worthless. Start implementing changes today, not tomorrow.' },
  ];

  const sansation = { fontFamily: "'Sansation', sans-serif" };

  const [imgError, setImgError] = useState(false);

  const imageSrc = "assets/images/FounderImage.jpeg";

  return (
    <>
      <style>{`
        .fn-root, .fn-root *, .fn-root *::before, .fn-root *::after {
          box-sizing: border-box;
          word-break: break-word;
        }
        .fn-root { 
          width: 100%; 
          overflow: hidden;
          max-width: 100vw;
        }
        .fn-author-quote-row { 
          display: flex; 
          align-items: flex-start; 
          gap: 28px; 
          margin-bottom: 28px; 
        }
        .fn-author { 
          display: flex; 
          flex-direction: column; 
          align-items: center; 
          text-align: center; 
          width: 130px; 
          flex-shrink: 0; 
        }
        .fn-quote-block { 
          flex: 1; 
          border-left: 2px solid #bfdbfe; 
          padding-left: 20px;
          min-width: 0;
        }
        .fn-steps { 
          display: grid; 
          grid-template-columns: repeat(3, minmax(0, 1fr)); 
          gap: 12px; 
          margin-top: 28px; 
        }

        /* Tablet adjustments */
        @media (max-width: 1023px) and (min-width: 640px) {
          .fn-author-quote-row {
            gap: 20px;
          }
          .fn-author {
            width: 110px;
          }
          .fn-quote-block {
            padding-left: 16px;
          }
          .fn-steps {
            gap: 10px;
          }
        }

        /* Mobile adjustments */
        @media (max-width: 639px) {
          .fn-author-quote-row { 
            flex-direction: column;
            align-items: center;
            gap: 20px;
            margin-bottom: 20px;
          }
          .fn-author {
            width: auto;
          }
          .fn-quote-block { 
            border-left: none;
            border-top: 2px solid #bfdbfe;
            padding-left: 0;
            padding-top: 16px;
            width: 100%;
          }
          .fn-steps { 
            grid-template-columns: 1fr; 
            gap: 10px;
            margin-top: 20px;
          }
        }

        /* Extra small mobile */
        @media (max-width: 400px) {
          .fn-author-quote-row {
            gap: 16px;
            margin-bottom: 16px;
          }
        }
      `}</style>

      <div className="fn-root">

        {/* Eyebrow — Sansation */}
        <div style={{ 
          ...sansation, 
          fontSize: isMobile ? 10 : 11, 
          fontWeight: 700, 
          letterSpacing: isMobile ? '0.1em' : '0.13em', 
          textTransform: 'uppercase', 
          color: '#1d4ed8', 
          marginBottom: isMobile ? 6 : 8 
        }}>
          {foundersNote?.eyebrow || "Founder's Note"}
        </div>

        {/* Subtitle — Sansation */}
        <div style={{ 
          ...sansation, 
          fontSize: isMobile ? 15 : (isTablet ? 17 : 'clamp(16px, 3.8vw, 20px)'), 
          fontStyle: 'italic', 
          color: '#64748b', 
          marginBottom: isMobile ? 20 : 28, 
          lineHeight: 1.3 
        }}>
          {foundersNote?.subtitle || 'From one founder to another..'}
        </div>

        <div className="fn-author-quote-row">
          <div className="fn-author">
      
            {/* Avatar */}
            {!imgError ? (
              <img
                src={imageSrc}
                alt="Founder"
                onError={() => setImgError(true)}
                style={{
                  width: isMobile ? 80 : 90,
                  height: isMobile ? 80 : 90,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2.5px solid #bfdbfe",
                  marginBottom: 10,
                }}
              />
            ) : (
              <div
                style={{
                  ...sansation,
                  width: isMobile ? 80 : 90,
                  height: isMobile ? 80 : 90,
                  borderRadius: "50%",
                  background: "#dbeafe",
                  border: "2.5px solid #bfdbfe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: isMobile ? 22 : 26,
                  fontWeight: 700,
                  color: "#1e3a8a",
                  marginBottom: 10,
                }}
              >
                {foundersNote?.authorInitials || "NP"}
              </div>
            )}

            {/* Name */}
            <div style={{ 
              fontSize: isMobile ? 13 : 14, 
              fontWeight: 700, 
              color: "#0f172a", 
              lineHeight: 1.3 
            }}>
              {foundersNote?.authorName || "Neeraj Parekh"}
            </div>

            {/* Role */}
            <div style={{ 
              fontSize: isMobile ? 10 : 11, 
              color: "#1d4ed8", 
              fontWeight: 600, 
              marginTop: 3 
            }}>
              {foundersNote?.authorRole || "Founder, CEO — DecX AI"}
            </div>
          </div>

          <div className="fn-quote-block">
            {/* Big quote — Sansation */}
            <div style={{ 
              ...sansation, 
              fontSize: isMobile ? 14 : (isTablet ? 15 : 17), 
              fontStyle: 'italic', 
              color: '#1d4ed8', 
              opacity: 0.45, 
              lineHeight: 1.4, 
              marginBottom: isMobile ? 12 : 16 
            }}>
              {foundersNote?.bigQuote ? `"${foundersNote.bigQuote}"` : '"Truth is the Fastest Path to Success"'}
            </div>

            {foundersNote?.principles?.length ? (
              foundersNote.principles.map((p, i) => (
                <p 
                  key={i} 
                  style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: '#334155', 
                    lineHeight: 1.75, 
                    marginBottom: isMobile ? 10 : 12 
                  }} 
                  dangerouslySetInnerHTML={{ __html: p }} 
                />
              ))
            ) : (
              <>
                <p style={{ 
                  fontSize: isMobile ? 13 : 14, 
                  color: '#334155', 
                  lineHeight: 1.75, 
                  marginBottom: isMobile ? 10 : 12 
                }}>
                  This report is built on a fundamental principle: <strong style={{ color: '#1d4ed8' }}>Brutal honesty serves you better than comfortable lies.</strong>
                </p>
                <p style={{ 
                  fontSize: isMobile ? 13 : 14, 
                  color: '#334155', 
                  lineHeight: 1.75, 
                  marginBottom: isMobile ? 10 : 12 
                }}>
                  Every criticism, every gap we identify, every harsh truth we present is designed with one goal: <strong style={{ color: '#1d4ed8' }}>To accelerate your path to funding success.</strong>
                </p>
              </>
            )}

            <p style={{ 
              fontStyle: 'italic', 
              fontSize: isMobile ? 11 : 12, 
              color: '#94a3b8', 
              borderTop: '1px solid #e2e8f0', 
              paddingTop: isMobile ? 10 : 14, 
              marginTop: isMobile ? 10 : 14, 
              marginBottom: 0 
            }}>
              {foundersNote?.campbell ? `"${foundersNote.campbell}"` : '"The cave you fear to enter holds the treasure you seek." — Joseph Campbell'}
            </p>
          </div>
        </div>

        <div className="fn-steps">
          {steps.map((step) => (
            <div 
              key={step.num} 
              style={{ 
                textAlign: 'center', 
                padding: isMobile ? '14px 10px' : (isTablet ? '16px 12px' : '18px 12px'), 
                background: '#ffffff', 
                border: '1px solid #e2e8f0', 
                borderRadius: isMobile ? 10 : 12 
              }}
            >
              <div style={{ 
                width: isMobile ? 28 : 30, 
                height: isMobile ? 28 : 30, 
                borderRadius: '50%', 
                background: '#16a34a', 
                color: '#fff', 
                fontSize: isMobile ? 13 : 14, 
                fontWeight: 700, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 10px' 
              }}>
                {step.num}
              </div>
              {/* Step title — Sansation */}
              <h5 style={{ 
                ...sansation, 
                fontSize: isMobile ? 11 : 12, 
                fontWeight: 700, 
                color: '#0f172a', 
                margin: '0 0 5px' 
              }}>
                {step.title}
              </h5>
              <p style={{ 
                fontSize: isMobile ? 10.5 : 11, 
                color: '#94a3b8', 
                lineHeight: 1.55, 
                margin: 0 
              }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>

      </div>
    </>
  );
};

export default FoundersNote;